import { useEffect, useState } from "react";

import { isCloudPrem } from "core/utils/deployment";
import { callServer, HttpMethod } from "store/utils/client";

type DefaultPricing = {
  annualPrice: number;
  monthlyPrice: number;
};

/**
 * Hardcoded default shown before the Stripe price fetch completes AND when the
 * fetch is skipped on cloud-prem. Callers that render these numbers visibly on
 * cloud-prem would leak SaaS-only pricing — gate the surface with `isCloudPrem()`
 * before use (see `AiQuotaPaywallModalBase` and `CreditCardGateCustom` for the
 * pattern).
 */
const FALLBACK: DefaultPricing = { annualPrice: 1200, monthlyPrice: 125 };

let globalCache: DefaultPricing | null = null;

export function useDefaultPricing(): DefaultPricing {
  const skipFetch = isCloudPrem();
  const [pricing, setPricing] = useState<DefaultPricing>(
    globalCache ?? FALLBACK,
  );

  useEffect(() => {
    if (skipFetch) return;
    if (globalCache) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await callServer<DefaultPricing>(
          { method: HttpMethod.Get, url: "v1/billing/default-pricing" },
          { notifyOnError: false },
        );
        if (!cancelled && response) {
          globalCache = response;
          setPricing(response);
        }
      } catch {
        // keep fallback
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skipFetch]);

  return pricing;
}
